/**
 * PillarIdentity — Roman numeral III, MFA-method stacked bar, people needing attention.
 *
 * Visual reference: 04-hybrid-recommended.html .pillar.identity block.
 * Data source: existing /userinsights/tenant/{id} endpoint (UserInsightsSummary).
 *
 * The MFA mix bar isn't a Recharts chart in the mockup — it's a hand-rolled
 * stacked bar with embedded counts. We keep that approach for fidelity: it
 * reads as a navigation cue, not a chart, and the percentages come straight
 * from the summary endpoint.
 */

import { UserGroupIcon } from '@heroicons/react/24/outline'
import { useTheme } from '@/context/ThemeContext'
import type { UserInsightsSummary } from '@/types'
import {
  PillarShell,
  HeroMetric,
  MiniGrid,
  ActionsBlock,
  Action,
} from './PillarShell'
import { IdentityEmpty } from './EmptyStates'

interface Props {
  insights: UserInsightsSummary | null | undefined
  isLoading: boolean
}

export default function PillarIdentity({ insights, isLoading }: Props) {
  useTheme()

  // Empty state: insights null (404 from API) OR hasData=false on the summary.
  const isEmpty = !isLoading && (!insights || !insights.hasData)

  if (!isEmpty) {
    // Mutually-exclusive MFA-method breakdown for enabled members. Each member
    // falls into exactly one bucket — phish-resistant (FIDO2 / Windows Hello /
    // Certificate), standard MFA only, or no MFA — so the segments truly sum
    // to the enabled-member total without double-counting overlapping methods.
    const total = insights?.enabledMemberUsers ?? 0
    const phishResistant = insights?.phishResistantMfaUsers ?? 0
    const mfaEnabled = insights?.mfaEnabledMembers ?? 0
    const standardMfa = Math.max(0, mfaEnabled - phishResistant)
    const noMfa = Math.max(0, total - mfaEnabled)

    const segments = [
      { label: 'Phish-resistant', count: phishResistant, color: 'rgb(var(--color-primary-500))' },
      { label: 'Standard MFA',    count: standardMfa,    color: 'rgb(var(--color-secondary-blue-300))' },
      { label: 'None',            count: noMfa,          color: '#E57373' },
    ]

    const adoptionPct = insights?.mfaMemberAdoptionPercentage ?? null
    const phishPct = Math.round((insights?.phishResistantMfaPercentage ?? 0) * 10) / 10

    // No real MFA-trend signal is wired through to this pillar yet, so we
    // leave the trend pill off rather than fabricate a static badge.
    const trend = null

    const licenseWaste = insights?.totalLicenseWaste ?? 0
    const dormant = insights?.inactiveMemberUsers ?? 0
    const noMfaCount = insights?.noMfaEnabledUsers ?? 0
    const needsAttention = insights?.needsAttentionUsers ?? 0
    const inactivityDays = insights?.inactivityThresholdDays ?? 90

    return (
      <PillarShell
        tone="identity"
        roman="III"
        eyebrow="Identity · Hygiene"
        Icon={UserGroupIcon}
        ctaLabel="Open identity"
        ctaHref="/user-insights"
      >
        <HeroMetric
          value={adoptionPct}
          unit="%"
          isLoading={isLoading}
          trend={trend}
        />

        <p className="mt-2 mb-6 text-[13px] leading-[1.55] text-slate-400">
          MFA coverage on enabled members.{' '}
          <strong className="font-bold text-slate-100 dark:text-slate-100">{phishPct}%</strong>{' '}
          are on phish-resistant methods (FIDO2, Windows Hello, certificate).
        </p>

        {/* Hand-rolled stacked MFA-method bar (not a Recharts chart). The
            percentages and counts read straight from the summary endpoint. */}
        <div className="mb-6 rounded-2xl bg-dark-800/60 px-4 py-3 dark:bg-white/[0.025]">
          <div className="mb-2 flex justify-between text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            <span>
              MFA mix · {total.toLocaleString()} user{total === 1 ? '' : 's'}
            </span>
            <span>by method</span>
          </div>
          <div className="flex h-5 overflow-hidden rounded-sm">
            {segments.map(s => {
              const pct = total > 0 ? (s.count / total) * 100 : 0
              if (pct < 0.5) return null
              return (
                <div
                  key={s.label}
                  className="grid place-items-center text-[9px] font-bold text-[#212938]"
                  style={{
                    width: `${pct}%`,
                    background: s.color,
                  }}
                  title={`${s.label}: ${s.count.toLocaleString()} (${pct.toFixed(0)}%)`}
                >
                  {pct >= 8 ? s.count.toLocaleString() : ''}
                </div>
              )
            })}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-x-4 gap-y-1 text-[10px] font-bold text-slate-500">
            {segments.map(s => {
              const pct = total > 0 ? Math.round((s.count / total) * 100) : 0
              return (
                <span key={s.label} className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="block h-2 w-2 rounded-sm"
                    style={{ background: s.color }}
                  />
                  <span className="text-slate-100 dark:text-slate-100">{s.label}</span>
                  <span>· {pct}%</span>
                </span>
              )
            })}
          </div>
        </div>

        <MiniGrid
          cells={[
            { label: 'No MFA', value: String(noMfaCount) },
            { label: 'Needs attention', value: String(needsAttention), suffix: 'flagged' },
            { label: `Dormant ${inactivityDays}+d`, value: String(dormant) },
            {
              // licenseWaste is a USER count (DisabledWithLicenses + InactiveWithLicenses),
              // not a currency figure. Render as users/seats — never $/mo. We don't have
              // SKU pricing data wired through this surface to compute recoverable spend.
              label: 'License waste',
              value: String(licenseWaste),
              suffix: licenseWaste === 1 ? 'user' : 'users',
            },
          ]}
        />

        <ActionsBlock label="People needing attention" allHref="/user-insights" ctaLabel="More people">
          {noMfaCount === 0 && dormant === 0 && licenseWaste === 0 ? (
            <p className="py-3 text-[12px] leading-[1.55] text-slate-400">
              Every enabled member has MFA, no dormant accounts, no license waste —
              your identity floor is solid.
            </p>
          ) : (
            <>
              {noMfaCount > 0 && (
                <Action
                  severity="crit"
                  title={`${noMfaCount} enabled user${noMfaCount === 1 ? '' : 's'} without MFA`}
                  meta="Enforce via Conditional Access or send a self-service enrolment link"
                  href="/user-insights"
                />
              )}
              {dormant > 0 && (
                <Action
                  severity="high"
                  title={`${dormant} member${dormant === 1 ? '' : 's'} inactive ${inactivityDays}+ days`}
                  meta="Disable, reclaim licenses, or remove if no longer needed"
                  href="/user-insights"
                />
              )}
              {licenseWaste > 0 && (
                <Action
                  severity="med"
                  title={`${licenseWaste.toLocaleString()} ${licenseWaste === 1 ? 'license' : 'licenses'} on disabled or inactive accounts`}
                  meta="Reassign or remove to free seats"
                  href="/user-insights"
                />
              )}
            </>
          )}
        </ActionsBlock>
      </PillarShell>
    )
  }

  return (
    <PillarShell
      tone="identity"
      roman="III"
      eyebrow="Identity · Hygiene"
      Icon={UserGroupIcon}
      ctaLabel="Open identity"
      ctaHref="/user-insights"
    >
      <IdentityEmpty ctaHref="/user-insights" />
    </PillarShell>
  )
}
